import "lazysizes";
import {
  configureLazySizes,
  initLazyMedia,
  initFeaturedCoverMedia,
} from "./utils/lazy-media";

import Swup from "swup";
import SwupScrollPlugin from "@swup/scroll-plugin";
import SwupBodyClassPlugin from "@swup/body-class-plugin";
import List from "list.js";

import { toggleView } from "./components/toggle-view";
import { collapsibleText } from "./components/collapsible-text";
import { floatingImages } from "./components/floating-images";
import { featuredProjectsParallax } from "./components/featured-projects-parallax";
import { featuredProjectsInView } from "./components/featured-projects-inview";
import Lightbox from "./components/lightbox";
import ArticleModal from "./components/article-modal";
import ProjectModal from "./components/project-modal";
import { initSenseGallery } from "./components/sense-gallery";
import { initCarousel } from "./components/carousel";
import { initStudioCarousel } from "./components/studio-carousel";
import { initStudioTeam } from "./components/studio-team";
import { applyReducedMotionVideos, prefersReducedMotion } from "./utils/reduced-motion";

console.log("LINKLAB. Design & Development by Matthias Deckx (https://matthiasdeckx.studio)");

configureLazySizes();

window.main = window.main || {};

function updateDocumentMeta(doc) {
  if (!doc) return;

  const title = doc.querySelector("title");
  if (title) {
    document.title = title.textContent;
  }

  const description = doc.querySelector('meta[name="description"]');
  if (description) {
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", description.getAttribute("content") || "");
  }

  const canonical = doc.querySelector('link[rel="canonical"]');
  if (canonical) {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical.getAttribute("href") || "");
  }
}

let isHomepage = false;
let isProjectPage = false;
let isProjectsPage = false;
let isShiftPage = false;
let isArticlePage = false;
let isSensePage = false;
let isStudioPage = false;
let isTouchDevice = false;
let isNavToggled = false;
let introRan = false;
let articleModal = null;
let projectModal = null;

function checks() {
  function checkIfTouchDevice() {
    return (
      true ==
      ("ontouchstart" in window ||
        (window.DocumentTouch && document instanceof DocumentTouch))
    );
  }

  if (checkIfTouchDevice() === true) {
    document.body.classList.add("is-touch");
    document.body.classList.add("is-mobile-device");
    isTouchDevice = true;
  } else {
    isTouchDevice = false;
  }

  if (document.body.classList.contains("page-home")) {
    isHomepage = true;
  } else {
    isHomepage = false;
  }

  if (document.body.classList.contains("page-project")) {
    isProjectPage = true;
  } else {
    isProjectPage = false;
  }

  if (document.body.classList.contains("page-projects")) {
    isProjectsPage = true;
  } else {
    isProjectsPage = false;
  }

  if (document.body.classList.contains("page-shift")) {
    isShiftPage = true;
  } else {
    isShiftPage = false;
  }

  if (document.body.classList.contains("page-article")) {
    isArticlePage = true;
  } else {
    isArticlePage = false;
  }

  if (document.body.classList.contains("page-sense")) {
    isSensePage = true;
  } else {
    isSensePage = false;
  }

  if (document.body.classList.contains("page-studio")) {
    isStudioPage = true;
  } else {
    isStudioPage = false;
  }

}

function initSortableList() {
  const projectsList = document.getElementById("projects-list");
  if (projectsList) {
    const options = { valueNames: ["title", "typology", "client", "year"] };
    new List("projects-list", options);
  }
}

function setupPlugins() {
  function initSwup() {
    const swup = new Swup({
      containers: ["#main"],
      animateHistoryBrowsing: true,
      animationSelector: '[class*="transition-fade"]',
      cache: true, // Enable caching for faster transitions
      skipPopStateHandling: false,
      linkSelector: 'a[href]:not([data-no-swup]):not(.c-shift-article__link):not(.c-featured-project__link):not(.c-projects-list__item-link)',
      plugins: [
        new SwupScrollPlugin({
          animateScroll: false,
        }),
        new SwupBodyClassPlugin({
          prefix: "page-",
        }),
      ],
    });

    // Store swup instance globally for debugging
    window.swup = swup;

    // Swup 4.0 uses hooks API
    if (swup.hooks) {
      const setPageTransitioning = (active) => {
        document.body.classList.toggle("is-page-transitioning", active);
      };

      swup.hooks.on("visit:start", (visit) => {
        if (prefersReducedMotion()) {
          visit.animation.animate = false;
        }
      });

      swup.hooks.on("animation:out:start", () => setPageTransitioning(true));
      swup.hooks.on("visit:end", () => setPageTransitioning(false));
      swup.hooks.on("visit:abort", () => setPageTransitioning(false));

      // Run immediately when content is replaced (during transition)
      swup.hooks.on("content:replace", (visit) => {
        // Only do critical updates immediately
        document.body.setAttribute("data-menu-open", "false");
        document.body.classList.remove("no-scroll");
        isNavToggled = false;
        checks(); // Update page type flags
        swupActiveLinks();
        if (visit?.to?.document) {
          updateDocumentMeta(visit.to.document);
        }
        applyReducedMotionVideos(document.getElementById("main"));
      });
      
      // Run after page transition completes (defer heavy operations)
      swup.hooks.on("page:view", () => {
        // Defer heavy initialization to avoid blocking transition
        requestAnimationFrame(() => {
          swupReload();
          swupActiveLinks(); // Set aria-current attributes first
          initMobileNav(); // Reinitialize to attach event listeners to new links
          
          // Update label - use multiple attempts to ensure it works
          updateMobileNavLabel();
          setTimeout(() => {
            updateMobileNavLabel();
          }, 100);
          setTimeout(() => {
            updateMobileNavLabel();
          }, 300);
          
          // Check if we should open a project modal after navigation
          const projectUrlToOpen = sessionStorage.getItem('openProjectModal');
          if (projectUrlToOpen) {
            sessionStorage.removeItem('openProjectModal');
            
            // Initialize project modal if needed
            if (!projectModal) {
              projectModal = new ProjectModal();
            }
            
            // Open the modal after a short delay
            setTimeout(() => {
              if (projectModal && projectUrlToOpen) {
                projectModal.open(projectUrlToOpen);
              }
            }, 300);
          }

          // Check if we should open an article modal after navigation
          const articleUrlToOpen = sessionStorage.getItem('openArticleModal');
          if (articleUrlToOpen) {
            sessionStorage.removeItem('openArticleModal');

            // Initialize article modal if needed
            if (!articleModal) {
              articleModal = new ArticleModal();
            }

            // Open the modal after a short delay
            setTimeout(() => {
              if (articleModal && articleUrlToOpen) {
                articleModal.open(articleUrlToOpen);
              }
            }, 300);
          }
        });
      });
    } else {
      console.error("Swup hooks API not available");
    }
  }

  initSwup();
}

function swupActiveLinks() {
  let currentPath = window.location.pathname;
  // Update both regular nav and mobile nav links
  let links = document.querySelectorAll("nav a, .c-nav-mobile__link");
  for (const link of links) {
    try {
      let linkPath = new URL(link.href).pathname;
      link.ariaCurrent = linkPath == currentPath ? "page" : false;
    } catch (e) {
      // Skip invalid URLs
      link.ariaCurrent = false;
    }
  }
}

function swupReload() {
  // Note: menu state and checks() are already handled in content:replace hook

  if (isHomepage) {
    initFeaturedCoverMedia(document.getElementById("main"));
  } else {
    initLazyMedia(document.getElementById("main"));
  }

  initSortableList();
  toggleView();
  collapsibleText();
  initFilters();
  initCarousel();
  applyReducedMotionVideos(document.getElementById("main"));

  if (!isTouchDevice) {
    floatingImages();
  }
  
  // Reinitialize featured projects parallax on homepage after Swup transition
  if (isHomepage) {
    featuredProjectsParallax();
    featuredProjectsInView();
    // Initialize project modal for featured projects
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    initProjectLinks();
  }

  if (isProjectPage) {
    const lb = new Lightbox({
      selector: '[data-rel="aiLightbox"]', // string
      lazyload: true, // boolean
      arrows: true, // boolean
      counter: true, // boolean
      slideSpeed: 500, //number(ms)
    });
  }

  if (isShiftPage) {
    if (!articleModal) {
      articleModal = new ArticleModal();
    }
    initArticleLinks();
  }

  if (isProjectsPage) {
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    initProjectLinks();
  }

  if (isSensePage) {
    initSenseGallery();
  }

  if (isStudioPage) {
    initStudioCarousel();
    initStudioTeam();
  }

}

function initArticleLinks() {
  const articleLinks = document.querySelectorAll('.c-shift-article__link');
  
  articleLinks.forEach(function(link) {
    // Check if listener already attached
    if (link.dataset.modalListener === 'true') {
      return; // Already has listener
    }
    
    link.dataset.modalListener = 'true';
    // Use capture phase to ensure our handler runs before Swup
    link.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      const url = this.getAttribute('href');
      if (articleModal && url) {
        articleModal.open(url);
      }
      return false;
    }, true); // true = capture phase
  });
}

function initProjectLinks() {
  // Initialize modal if it doesn't exist
  if (!projectModal) {
    projectModal = new ProjectModal();
  }
  
  // Handle both projects list links and featured project links
  const projectLinks = document.querySelectorAll('.c-projects-list__item-link, .c-featured-project__link');
  
  if (projectLinks.length === 0) {
    return; // No project links found
  }
  
  projectLinks.forEach(function(link) {
    // Check if listener already attached
    if (link.dataset.modalListener === 'true') {
      return; // Already has listener
    }
    
    link.dataset.modalListener = 'true';
    link.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Prevent Swup from intercepting
      const url = this.getAttribute('href');
      if (projectModal && url) {
        projectModal.open(url);
      }
    }, true); // Use capture phase to run before Swup
  });
}

function initMobileNav() {
  const mobileNavToggle = document.querySelector(".js-nav-mobile-toggle");
  const mobileNav = document.querySelector(".c-nav-mobile");
  
  if (!mobileNavToggle || !mobileNav) return;
  
  // Remove old listener by cloning (clean way to remove all listeners)
  const newToggle = mobileNavToggle.cloneNode(true);
  mobileNavToggle.parentNode.replaceChild(newToggle, mobileNavToggle);
  
  // Get fresh reference after replacement
  const toggle = document.querySelector(".js-nav-mobile-toggle");
  const nav = document.querySelector(".c-nav-mobile");
  
  if (!toggle || !nav) return;
  
  // Attach toggle listener with fresh references
  toggle.addEventListener("click", function(e) {
    e.preventDefault();
    e.stopPropagation();
    // Always get fresh references inside handler
    const currentNav = document.querySelector(".c-nav-mobile");
    const currentToggle = document.querySelector(".js-nav-mobile-toggle");
    if (currentNav && currentToggle) {
      currentNav.classList.toggle("is-open");
      const isOpen = currentNav.classList.contains("is-open");
      currentToggle.setAttribute("aria-expanded", isOpen);
    }
  });
  
  // Attach listeners to nav links (these get replaced by Swup, so always reattach)
  const navLinks = document.querySelectorAll(".c-nav-mobile__link");
  navLinks.forEach(function(link) {
    link.addEventListener("click", function() {
      // Always get fresh references inside handler
      const currentNav = document.querySelector(".c-nav-mobile");
      const currentToggle = document.querySelector(".js-nav-mobile-toggle");
      if (currentNav && currentToggle) {
        currentNav.classList.remove("is-open");
        currentToggle.setAttribute("aria-expanded", "false");
      }
    });
  });
  
  // Close dropdown when clicking outside (only add once globally)
  if (!window.mobileNavOutsideClickHandler) {
    window.mobileNavOutsideClickHandler = function(e) {
      // Always get fresh references
      const currentNav = document.querySelector(".c-nav-mobile");
      const currentToggle = document.querySelector(".js-nav-mobile-toggle");
      if (currentNav && currentToggle && !currentNav.contains(e.target)) {
        currentNav.classList.remove("is-open");
        currentToggle.setAttribute("aria-expanded", "false");
      }
    };
    document.addEventListener("click", window.mobileNavOutsideClickHandler);
  }
}

function updateMobileNavLabel() {
  const mobileNavToggle = document.querySelector(".js-nav-mobile-toggle");
  if (!mobileNavToggle) return;
  
  const currentPath = window.location.pathname;
  const isHomepage = document.body.classList.contains("page-home");
  
  let labelText = "Menu";
  
  // First, try to find active link by aria-current
  let activeLink = document.querySelector(".c-nav-mobile__link[aria-current='page']");
  
  if (activeLink) {
    labelText = activeLink.textContent.trim();
  } else {
    // If no aria-current, try regular nav
    const regularActiveLink = document.querySelector(".c-nav-item a[aria-current='page']");
    if (regularActiveLink) {
      labelText = regularActiveLink.textContent.trim();
    } else if (!isHomepage) {
      // Match by URL - check mobile nav links first
      const allMobileLinks = document.querySelectorAll(".c-nav-mobile__link");
      let found = false;
      
      for (let link of allMobileLinks) {
        try {
          const linkPath = new URL(link.href).pathname;
          // Check for exact match or if current path starts with link path
          if (linkPath === currentPath || (linkPath !== '/' && currentPath.startsWith(linkPath))) {
            labelText = link.textContent.trim();
            found = true;
            break;
          }
        } catch (e) {
          continue;
        }
      }
      
      // If not found in mobile nav, check regular nav links
      if (!found) {
        const allRegularLinks = document.querySelectorAll(".c-nav-item a");
        for (let link of allRegularLinks) {
          try {
            const linkPath = new URL(link.href).pathname;
            if (linkPath === currentPath || (linkPath !== '/' && currentPath.startsWith(linkPath))) {
              labelText = link.textContent.trim();
              found = true;
              break;
            }
          } catch (e) {
            continue;
          }
        }
      }
      
      // Final fallback: try to get from page title
      if (!found && labelText === "Menu") {
        const pageTitle =
          document.querySelector("h1.u-sr-only") ||
          document.querySelector(".c-filters__heading-text");
        if (pageTitle) {
          labelText = pageTitle.textContent.trim();
        }
      }
    }
  }
  
  // Update the button text only if we have a valid label
  if (labelText && labelText !== "Menu" || isHomepage) {
    mobileNavToggle.textContent = labelText;
  }
}

function initFilters() {
  const filtersToggle = document.querySelector(".js-filters-toggle");
  const filters = document.querySelector(".c-filters");
  const filterLinks = document.querySelectorAll(".js-filter-link");
  const projectItems = document.querySelectorAll(".js-item-to-filter");
  
  if (filtersToggle && filters) {
    filtersToggle.addEventListener("click", function(e) {
      e.preventDefault();
      e.stopPropagation();
      filters.classList.toggle("is-open");
      const isOpen = filters.classList.contains("is-open");
      filtersToggle.setAttribute("aria-expanded", isOpen);
    });
    
    // Close dropdown when clicking outside
    document.addEventListener("click", function(e) {
      if (!filters.contains(e.target)) {
        filters.classList.remove("is-open");
        filtersToggle.setAttribute("aria-expanded", "false");
      }
    });
  }
  
  // Helper function to slugify a string
  function slugify(text) {
    return text.toString().toLowerCase()
      .replace(/\s+/g, '-')           // Replace spaces with -
      .replace(/[^\w\-]+/g, '')       // Remove all non-word chars
      .replace(/\-\-+/g, '-')         // Replace multiple - with single -
      .replace(/^-+/, '')             // Trim - from start of text
      .replace(/-+$/, '');            // Trim - from end of text
  }
  
  // Track selected filters
  let selectedFilters = new Set();
  
  // Function to update filter display
  function updateFilterDisplay() {
    projectItems.forEach(function(item) {
      const typology = item.getAttribute("data-typology") || "";
      
      if (selectedFilters.size === 0) {
        // Show all projects when no filters selected
        item.classList.remove("is-hidden");
        item.style.display = "";
      } else {
        // Typology might be comma-separated, convert each to slug and check
        const typologyValues = typology.split(',').map(function(t) {
          return slugify(t.trim());
        });
        
        // Check if item matches any selected filter
        const matchesFilter = Array.from(selectedFilters).some(function(filterValue) {
          return typologyValues.includes(filterValue);
        });
        
        if (matchesFilter) {
          item.classList.remove("is-hidden");
          item.style.display = "";
        } else {
          item.classList.add("is-hidden");
          item.style.display = "none";
        }
      }
    });
  }
  
  // Filter projects when clicking a filter link
  if (filterLinks.length > 0 && projectItems.length > 0) {
    filterLinks.forEach(function(link) {
      link.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        // Get filter value (slug)
        const filterValue = this.getAttribute("data-filter");
        
        // Toggle filter selection
        if (this.classList.contains("is-active")) {
          // Deselect: remove from active filters
          this.classList.remove("is-active");
          selectedFilters.delete(filterValue);
        } else {
          // Select: add to active filters
          this.classList.add("is-active");
          selectedFilters.add(filterValue);
        }
        
        // Update filter display
        updateFilterDisplay();
        
        // Keep dropdown open - don't close it
      });
    });
    
    // Apply initial filter if URL has a category parameter
    const urlParams = new URLSearchParams(window.location.search);
    const categoryParam = urlParams.get('categories');
    if (categoryParam) {
      const categorySlugs = categoryParam.split(',').map(function(c) {
        return c.trim();
      });
      categorySlugs.forEach(function(slug) {
        const matchingLink = Array.from(filterLinks).find(function(link) {
          return link.getAttribute("data-filter") === slug;
        });
        if (matchingLink) {
          matchingLink.classList.add("is-active");
          selectedFilters.add(slug);
        }
      });
      updateFilterDisplay();
    }
  }
}

function init() {
  if (history.scrollRestoration) {
    history.scrollRestoration = "manual";
  }

  // Check if we're on an article page and should redirect to shift page with modal
  if (isArticlePage) {
    const currentUrl = window.location.href;
    const shiftUrl = window.location.origin + '/shift';

    // Store the article URL in sessionStorage to open modal after navigation
    sessionStorage.setItem('openArticleModal', currentUrl);

    // Navigate to shift page
    // Use Swup if available (wait a bit for it to be ready), otherwise use window.location
    const navigateToShift = () => {
      if (window.swup && typeof window.swup.navigate === 'function') {
        window.swup.navigate(shiftUrl);
      } else {
        window.location.href = shiftUrl;
      }
    };

    // Try immediately, or wait a bit for Swup to be ready
    if (window.swup) {
      navigateToShift();
    } else {
      // Wait a bit for Swup to initialize
      setTimeout(navigateToShift, 100);
    }

    // Don't continue initialization - navigation will trigger new page load
    return;
  }

  // Check if we're on a project page and should redirect to projects page with modal
  if (isProjectPage) {
    const currentUrl = window.location.href;
    const projectsUrl = window.location.origin + '/projects';
    
    // Store the project URL in sessionStorage to open modal after navigation
    sessionStorage.setItem('openProjectModal', currentUrl);
    
    // Navigate to projects page
    // Use Swup if available (wait a bit for it to be ready), otherwise use window.location
    const navigateToProjects = () => {
      if (window.swup && typeof window.swup.navigate === 'function') {
        window.swup.navigate(projectsUrl);
      } else {
        window.location.href = projectsUrl;
      }
    };
    
    // Try immediately, or wait a bit for Swup to be ready
    if (window.swup) {
      navigateToProjects();
    } else {
      // Wait a bit for Swup to initialize
      setTimeout(navigateToProjects, 100);
    }
    
    // Don't continue initialization - navigation will trigger new page load
    return;
  }
  
  // Check if we should open a project modal after navigation
  const projectUrlToOpen = sessionStorage.getItem('openProjectModal');
  if (projectUrlToOpen && isProjectsPage) {
    sessionStorage.removeItem('openProjectModal');
    
    // Initialize project modal
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    
    // Open the modal after a short delay to ensure page is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (projectModal && projectUrlToOpen) {
          projectModal.open(projectUrlToOpen);
        }
      }, 300);
    });
  }

  // Check if we should open an article modal after navigation
  const articleUrlToOpen = sessionStorage.getItem('openArticleModal');
  if (articleUrlToOpen && isShiftPage) {
    sessionStorage.removeItem('openArticleModal');

    // Initialize article modal
    if (!articleModal) {
      articleModal = new ArticleModal();
    }

    // Open the modal after a short delay to ensure page is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (articleModal && articleUrlToOpen) {
          articleModal.open(articleUrlToOpen);
        }
      }, 300);
    });
  }

  const introelement = document.getElementById("intro");
  if (typeof introelement != "undefined" && introelement != null && typeof intro === "function") {
    intro(introRan, isHomepage);
    introRan = true;
  }

  initSortableList();
  toggleView();
  collapsibleText();
  initFilters();
  initMobileNav();
  updateMobileNavLabel();
  initCarousel();
  initProjectLinks(); // Always try to initialize project links if they exist
  if (!isTouchDevice) {
    floatingImages();
  }
  
  // Initialize featured projects parallax on homepage
  if (isHomepage) {
    featuredProjectsParallax();
    featuredProjectsInView();
    // Initialize project modal for featured projects
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    initProjectLinks();
  }

  // Only initialize lightbox if we're still on a project page (shouldn't happen after redirect)
  if (isProjectPage) {
    const lb = new Lightbox({
      selector: '[data-rel="aiLightbox"]', // string
      lazyload: true, // boolean
      arrows: true, // boolean
      counter: true, // boolean
      slideSpeed: 500, //number(ms)
    });
  }

  if (isShiftPage) {
    if (!articleModal) {
      articleModal = new ArticleModal();
    }
    initArticleLinks();
  }

  if (isProjectsPage) {
    if (!projectModal) {
      projectModal = new ProjectModal();
    }
    initProjectLinks();
  }

  if (isSensePage) {
    initSenseGallery();
  }

  if (isStudioPage) {
    initStudioCarousel();
    initStudioTeam();
  }

  function setOffsetPosition() {
    const logoElement = document.querySelector(".js-logo-part");
    if (logoElement) {
      const logoStyles = getComputedStyle(logoElement);
      const logoHeight = logoElement.offsetHeight + parseFloat(logoStyles.marginTop) + parseFloat(logoStyles.marginBottom);
      //Set 'top' of sticky element to the offsetheight of logo
      const elements = document.querySelectorAll(".js-stick-offset-logo");
      elements.forEach((element) => {
        element.style.top = logoHeight + "px";
      });
    }
  }

  function fadeOnScroll() {
    const logoElement = document.querySelector(".js-logo-part");
    if (logoElement) {
      const logoHeight = document.querySelector(".js-logo-part").offsetHeight;
      const elements = document.querySelectorAll(".js-fade-on-scroll");
      elements.forEach((element) => {
        const scrollPosition = window.scrollY;
        const opacity = 1 - scrollPosition / (logoHeight / 1.5);
        element.style.opacity = opacity;
      });
    }
  }

  setOffsetPosition();
  window.addEventListener("resize", setOffsetPosition);
  window.addEventListener("scroll", fadeOnScroll);

  function projectNav() {
    // Title is now in modal header (fixed position), no scroll-based opacity needed
  }

  if (isProjectPage) {
    projectNav();
  }

  const projectTitles = document.querySelectorAll('.c-project-modal__title');
  projectTitles.forEach((titleEl) => {
    const text = titleEl.textContent?.trim() || '';
    titleEl.classList.toggle('is-long', text.length > 20);
  });

  applyReducedMotionVideos();
  if (isHomepage) {
    initFeaturedCoverMedia(document.getElementById("main"));
  } else {
    initLazyMedia(document.getElementById("main"));
  }
}

// Ensure DOM is ready before initializing
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    checks();
    setupPlugins();
    init();
  });
} else {
  // DOM is already ready
  checks();
  setupPlugins();
  init();
}
